import { useMemo } from "react";
import { TableColumnsType, TableColumnType } from "antd";
import {
  TableData,
  getSortableString,
  createFiltersForColumn,
} from "views/uikit/VLTable/tableUtils";
import { FolderMetadata, FolderStatus, MetadataFieldKeys } from "types";
import StatusCell, {
  StatusColor,
} from "views/uikit/VLTable/TableCells/StatusCell/StatusCell";

export interface FolderTableData extends TableData {
  key: string;
  folderName: string;
  status: FolderStatus;
  invalidReason: string | null;
  totalImages: number;
  progress: number;
  [metadataKey: string]: string | null | number;
}

const metadataKeys = Object.values(MetadataFieldKeys);

function getMetadataTitle(key: MetadataFieldKeys): string {
  const aliases: Record<MetadataFieldKeys, string> = {
    [MetadataFieldKeys.JOB_NAME]: "Job",
    [MetadataFieldKeys.SETUP_NAME]: "Setup",
    [MetadataFieldKeys.RECIPES_NAME]: "Recipe List",
  };

  return aliases[key];
}

function flattenFolder(
  folder: FolderMetadata,
  metadataKeys: string[],
): FolderTableData {
  const row: FolderTableData = {
    key: folder.id,
    folderName: folder.folder_name,
    status: folder.status,
    invalidReason: folder.invalid_reason,
    totalImages: folder.total_images,
    progress: folder.upload_percentage,
  };

  metadataKeys.forEach((key) => {
    if (!folder.metadata) {
      // Show "pending..." when metadata is not available yet, but "-" for invalid folders
      row[key] = folder.status === "invalid" ? "-" : "pending...";
    } else {
      const value = folder.metadata[key];
      row[key] = Array.isArray(value)
        ? value.map((item) => `"${item}"`).join(", ")
        : String(value ?? ""); //handle string and string[]
    }
  });

  return row;
}

const Status = ({ record }: { record: FolderTableData }) => {
  const { status: value, invalidReason } = record;

  const getStatusColorByFolderStatus: Record<FolderStatus, StatusColor> = {
    uploading: "processing",
    validating: "warning",
    valid: "success",
    invalid: "error",
  };

  const NewStatus = (value: FolderStatus, progress: number | null) => (
    <div style={{ display: "flex", alignItems: "center" }}>
      <StatusCell
        color={getStatusColorByFolderStatus[value]}
        text={progress != null ? `${progress}% ${value}` : value}
        loading={value === "uploading" || value === "validating"}
        tooltip={
          value === "invalid" && invalidReason
            ? {
                title: "Invalid Reason:",
                text: invalidReason,
              }
            : undefined
        }
      />
    </div>
  );

  return NewStatus(value, value === "uploading" ? record.progress : null);
};

export default function useFolderTableData(
  folders: FolderMetadata[],
  filterState: Record<string, string[]> = {},
  allFolders: FolderMetadata[] = folders, // All folders for filter options
) {
  const dataSource = useMemo(
    () => folders.map((folder) => flattenFolder(folder, metadataKeys)),
    [folders],
  );

  // Create filter data source from ALL folders (not just filtered ones)
  const filterDataSource = useMemo(() => {
    return allFolders.map((folder) => {
      const row: Record<string, any> = {
        folderName: folder.folder_name,
        status: folder.status,
      };

      metadataKeys.forEach((key) => {
        row[key] = folder.metadata?.[key]; // Keep original arrays for filter creation
      });

      return row;
    });
  }, [allFolders]);

  const columns: TableColumnsType<FolderTableData> = useMemo(() => {
    // Calculate dynamic width distribution to total 100%
    const statusWidth = 13;
    const folderWidth = 25;
    const totalItemsWidth = 10;
    const remainingWidth = 100 - folderWidth - statusWidth - totalItemsWidth; // Available for metadata columns
    const metadataWidth =
      metadataKeys.length > 0
        ? Math.floor(remainingWidth / metadataKeys.length)
        : 0;

    // Folder column (always first)
    const folderColumn: TableColumnType<FolderTableData> = {
      key: "folderName",
      title: "Folder",
      dataIndex: "folderName",
      width: `${folderWidth}%`,
      sorter: (a, b) =>
        getSortableString(a.folderName).localeCompare(
          getSortableString(b.folderName),
        ),
    };

    // Metadata columns (in the middle)
    const metadataColumns: TableColumnType<FolderTableData>[] =
      metadataKeys.map((key) => ({
        key,
        dataIndex: key,
        title: getMetadataTitle(key),
        width: `${metadataWidth}%`,
        sorter: (a, b) =>
          getSortableString(a[key]).localeCompare(getSortableString(b[key])),
        filterSearch: true,
        filters: createFiltersForColumn(filterDataSource as TableData[], key),
        filteredValue: filterState[key] || null,
      }));

    const totalItemsColumn: TableColumnType<FolderTableData> = {
      key: "totalItems",
      title: "Size",
      dataIndex: "totalItems",
      width: `${totalItemsWidth}%`,
      render: (_value, record) => record.totalImages,
    };

    // Status column (always last)
    const statusColumn: TableColumnType<FolderTableData> = {
      key: "status",
      title: "Status",
      dataIndex: "status",
      width: `${statusWidth}%`,
      sorter: (a, b) =>
        getSortableString(a.status).localeCompare(getSortableString(b.status)),
      filterSearch: true,
      filters: createFiltersForColumn(
        filterDataSource as TableData[],
        "status",
      ),
      filteredValue: filterState["status"] || null,
      render: (value: FolderStatus, record: FolderTableData) => (
        <Status record={record} />
      ),
    };

    return [folderColumn, ...metadataColumns, totalItemsColumn, statusColumn];
  }, [filterState, filterDataSource]);

  return { columns, dataSource };
}
